// landing-editor.component.ts
import { Component, OnInit, signal, computed, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LandingPageService } from '../../services/landing.service';
import { LandingS3UploadService } from '../../services/landing-s3-upload.service';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { LandingPreviewComponent } from './components/landing-preview/landing-preview.component';
import { LandingPreviewData } from '../../models/landing-preview.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'landing-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NavbarComponent,
    FooterComponent,
    LandingPreviewComponent
  ],
  templateUrl: './landing-editor.component.html'
})
export class LandingEditorComponent implements OnInit {

  private readonly fb = inject(FormBuilder);
  private readonly landingService = inject(LandingPageService);
  private readonly s3Service = inject(LandingS3UploadService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // Form
  landingForm!: FormGroup;

  // Signals para reactividad
  readonly uploadProgress = signal<number>(0);
  readonly uploadingCount = signal<number>(0);
  readonly slugValue = signal<string>('');
  readonly isSubmitting = signal<boolean>(false);
  readonly submitError = signal<string>('');
  readonly submitSuccess = signal<string>('');
  readonly previewUrl = signal<string>('');
  readonly isEditMode = signal<boolean>(false);

  //Signal para los datos de preview
  readonly previewData = signal<LandingPreviewData | null>(null);

  //Signal para toggle de vista
  readonly showPreview = signal<boolean>(true);
  currentPageId: string = '';

  // Computed
  readonly slugPreview = computed(() => {
    const slug = this.slugValue();
    return slug ? `/sitios/${slug}` : '';
  });

  // Configuración
  currentOrgId: string = '';

  constructor() {
    // Effect para actualizar preview en tiempo real
    effect(() => {
      if (this.landingForm) {
        this.updatePreviewData();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    // Capturar org_id de la URL
    const alojamientoId = this.route.snapshot.params['id'];

    console.log('📍 ===== LANDING EDITOR INIT =====');
    console.log('📍 URL completa:', this.router.url);
    console.log('📍 Parámetro capturado:', alojamientoId);

    if (alojamientoId) {
      this.currentOrgId = alojamientoId;
      console.log('📍 currentOrgId asignado:', this.currentOrgId);
    } else {
      console.error('❌ No se capturó el parámetro id de la URL');
    }

    // 👇 NUEVO: Detectar modo edición
    const slug = this.route.snapshot.queryParams['slug'];
    const pageId = this.route.snapshot.queryParams['pageId'];

    console.log('🔍 Query params:', { slug, pageId });

    if(pageId){
      this.currentPageId = pageId;
      console.log('💾 PageId capturado de query params:', this.currentPageId);
    }

    // Inicializar formulario primero
    this.initForm();
    this.setupSlugPreview();
    this.setupPreviewListener();

    // Si hay slug, cargar datos para editar
    if (slug) {
      console.log('✏️ Modo EDICIÓN detectado - Cargando landing...');
      this.isEditMode.set(true);
      this.loadExistingLanding(slug);
    } else {
      console.log('➕ Modo CREACIÓN detectado');
      this.isEditMode.set(false);
    }

    console.log('📍 ================================');
  }

  async loadExistingLanding(slug: string): Promise<void> {
    try {
      console.log('📥 Cargando landing con slug:', slug);

      // Obtener la landing por slug
      const response = await this.landingService.getPageBySlug(slug).toPromise();

      console.log('✅ Respuesta del servidor:', response);

      // La respuesta puede venir como response.page o directamente response
      const page = response.page || response;

      if (!page) {
        console.error('❌ No se encontró la landing page');
        this.submitError.set('No se encontró la landing page');
        return;
      }

      console.log('📄 Datos de la página:', page);

      this.currentPageId = page.page_id;

      // Pre-llenar formulario con los datos existentes
      this.landingForm.patchValue({
        title: page.title || '',
        slug: page.slug || '',
        hero_title: page.content_json?.hero?.title || page.title || '',
        hero_subtitle: page.content_json?.hero?.subtitle || '',
        main_description: page.content_json?.sections?.[0]?.content || '',
        custom_html: page.custom_html || '',
        meta_title: page.seo_tags?.meta_title || '',
        meta_description: page.seo_tags?.meta_description || '',
        keywords: Array.isArray(page.seo_tags?.keywords)
          ? page.seo_tags.keywords.join(', ')
          : ''
      });

      console.log('✅ Formulario pre-llenado');

      // Cargar imágenes existentes
      if (page.images && Array.isArray(page.images)) {
        console.log('🖼️ Cargando', page.images.length, 'imágenes existentes');

        page.images.forEach((img: any) => {
          const imageGroup = this.fb.group({
            file: [null], // No hay archivo físico, ya está en S3
            alt_text: [img.alt_text || ''],
            preview: [img.url || ''], // URL de S3 para preview
            uploaded_url: [img.url || ''] // Ya está subida
          });

          this.images.push(imageGroup);
        });

        console.log('✅ Imágenes cargadas en el formulario');
      }

      // Actualizar preview
      this.updatePreviewData();

      console.log('✅ Landing cargada completamente para edición');

    } catch (error) {
      console.error('❌ Error cargando landing para edición:', error);
      this.submitError.set('Error al cargar la landing page: ' + (error || 'Error desconocido'));
    }
  }

  private initForm(): void {
    this.landingForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
      hero_title: ['', Validators.required],
      hero_subtitle: [''],
      main_description: ['', Validators.required],
      meta_title: [''],
      meta_description: ['', Validators.maxLength(160)],
      og_image: [''],
      keywords: [''],
      custom_html: [''],
      images: this.fb.array([])
    });
  }

  //listener para cambios en el formulario
  private setupPreviewListener(): void {
    this.landingForm.valueChanges.subscribe(() => {
      this.updatePreviewData();
    });
  }

  //Actualizar datos de preview

  private updatePreviewData(): void {
    const formValue = this.landingForm.value;

    // Obtener la primera imagen con preview
    const heroImage = this.images.length > 0
      ? {
        url: this.images.at(0).get('preview')?.value || '',
        alt: this.images.at(0).get('alt_text')?.value || 'Imagen principal'
      }
      : undefined;

    // Construir array de imágenes
    const galleryImages = this.images.controls
      .map((control, index) => ({
        url: control.get('preview')?.value || '',
        alt_text: control.get('alt_text')?.value || `Imagen ${index + 1}`
      }))
      .filter(img => img.url); // Solo incluir imágenes con preview

    const previewData: LandingPreviewData = {
      title: formValue.title || 'Título de la página',
      slug: formValue.slug || '',
      hero: {
        title: formValue.hero_title || formValue.title || 'Título Principal',
        subtitle: formValue.hero_subtitle || '',
        imageUrl: heroImage?.url,
        imageAlt: heroImage?.alt
      },
      content: {
        description: formValue.main_description || ''
      },
      images: galleryImages,
      customHtml: formValue.custom_html || '',
      seo: {
        meta_title: formValue.meta_title || formValue.title || '',
        meta_description: formValue.meta_description || '',
        keywords: formValue.keywords
          ? formValue.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
          : []
      },
      orgId: this.currentOrgId
    };

    this.previewData.set(previewData);
  }

  //Toggle de vista previa
  togglePreview(): void {
    this.showPreview.update(v => !v);
  }

  get images(): FormArray {
    return this.landingForm.get('images') as FormArray;
  }

  private setupSlugPreview(): void {
    // Actualizar el signal cuando cambia el slug
    this.landingForm.get('slug')?.valueChanges.subscribe(value => {
      const sanitized = this.landingService.sanitizeSlug(value || '');
      this.slugValue.set(sanitized);
    });

    // Auto-generar slug desde título
    this.landingForm.get('title')?.valueChanges.subscribe(value => {
      const currentSlug = this.landingForm.get('slug')?.value;

      if (!currentSlug || currentSlug === '') {
        const autoSlug = this.landingService.sanitizeSlug(value || '');
        this.landingForm.patchValue({ slug: autoSlug }, { emitEvent: true });
      }
    });
  }

  generateSlugFromTitle(): void {
    const title = this.landingForm.get('title')?.value;
    if (!title) return;

    const slug = this.landingService.sanitizeSlug(title);
    this.landingForm.patchValue({ slug });

    // Auto-llenar hero_title si está vacío
    if (!this.landingForm.get('hero_title')?.value) {
      this.landingForm.patchValue({ hero_title: title });
    }
  }

  addImage(): void {
    const imageGroup = this.fb.group({
      file: [null, Validators.required],
      alt_text: ['', Validators.required],
      preview: [''],
      uploaded_url: ['']
    });

    this.images.push(imageGroup);
  }

  removeImage(index: number): void {
    this.images.removeAt(index);
  }

  onFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Validaciones
    if (!file.type.startsWith('image/')) {
      this.submitError.set('Por favor selecciona un archivo de imagen válido');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.submitError.set('La imagen no debe superar 5MB');
      return;
    }

    // Guardar archivo
    this.images.at(index).patchValue({ file });

    // Generar preview
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.images.at(index).patchValue({ preview: e.target?.result });
    };
    reader.readAsDataURL(file);

    // Sugerir alt text desde nombre del archivo
    const suggestedAlt = this.generateAltTextFromFileName(file.name);

    if (!this.images.at(index).get('alt_text')?.value) {
      this.images.at(index).patchValue({ alt_text: suggestedAlt });
    }
  }

  private generateAltTextFromFileName(fileName: string): string {
    return fileName
      .replace(/\.[^/.]+$/, '') // Quitar extensión
      .replace(/[_-]/g, ' ')    // Reemplazar guiones/underscores
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalizar
      .trim();
  }

  private buildContentJson(): any {
    const formValue = this.landingForm.value;

    return {
      hero: {
        title: formValue.hero_title || formValue.title,
        subtitle: formValue.hero_subtitle || ''
      },
      sections: [
        {
          type: 'text',
          content: formValue.main_description
        }
      ]
    };
  }

  private buildSeoTags(uploadedImages: any[]): any {
    const formValue = this.landingForm.value;

    const keywordsArray = formValue.keywords
      ? formValue.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
      : [];

    //Auto Asignación de og_image con la primera imagen que se suba
    const ogImage = uploadedImages && uploadedImages.length > 0
      ? uploadedImages[0].url
      : (formValue.og_image || '')

    return {
      meta_title: formValue.meta_title || formValue.title,
      meta_description: formValue.meta_description || '',
      og_image: ogImage,
      keywords: keywordsArray
    };
  }

  /**
   * Sube imágenes con barra de progreso
   */
  private async uploadImagesWithProgress(): Promise<any[]> {
    const archivos: File[] = [];
    const altTexts: string[] = [];

    // Recolectar archivos y alt texts
    for (let i = 0; i < this.images.length; i++) {
      const imageControl = this.images.at(i);
      const file = imageControl.get('file')?.value;
      const alt_text = imageControl.get('alt_text')?.value;

      if (file) {
        archivos.push(file);
        altTexts.push(alt_text);
      }
    }

    if (archivos.length === 0) {
      throw new Error('No hay imágenes para subir');
    }

    try {
      console.log('📤 Subiendo imágenes a S3...');

      // Subir usando el servicio
      const uploadedUrls = await this.s3Service.subirVariasImagenes(
        archivos,
        this.currentOrgId,
        (progresoTotal, completadas) => {
          this.uploadProgress.set(progresoTotal);
          this.uploadingCount.set(completadas);
          console.log(`📊 ${progresoTotal.toFixed(0)}% (${completadas}/${archivos.length})`);
        }
      );

      // Combinar URLs con alt texts
      const uploadedImages = uploadedUrls.map((resultado, index) => ({
        url: resultado.url,
        alt_text: altTexts[index]
      }));

      console.log('✅ Imágenes subidas exitosamente');
      return uploadedImages;

    } catch (error) {
      console.error('❌ Error subiendo imágenes:', error);
      throw new Error('No se pudieron subir las imágenes a S3');
    }
  }

  /**
   * Resetea el formulario
   */
  resetForm(): void {
    this.landingForm.reset();
    this.images.clear();
    this.submitError.set('');
    this.submitSuccess.set('');
    this.slugValue.set('');
    this.previewUrl.set('');
    this.uploadProgress.set(0);
    this.uploadingCount.set(0);
    this.previewData.set(null);
  }

  /**
   * Submit principal
   */
  async onSubmit(): Promise<void> {
  // Validar formulario
  if (this.landingForm.invalid) {
    this.landingForm.markAllAsTouched();
    this.submitError.set('Por favor completa todos los campos obligatorios');
    return;
  }

  // Validar que haya imágenes
  if (this.images.length === 0) {
    this.submitError.set('Debes agregar al menos una imagen');
    return;
  }

  // Reset estados
  this.isSubmitting.set(true);
  this.submitError.set('');
  this.submitSuccess.set('');
  this.uploadProgress.set(0);

  try {
    // 1. Subir SOLO las imágenes NUEVAS (que tienen file)
    const newImages: any[] = [];
    const existingImages: any[] = [];

    for (let i = 0; i < this.images.length; i++) {
      const imageControl = this.images.at(i);
      const file = imageControl.get('file')?.value;
      const uploadedUrl = imageControl.get('uploaded_url')?.value;

      if (file) {
        // Imagen nueva que necesita subirse
        newImages.push({
          file,
          alt_text: imageControl.get('alt_text')?.value
        });
      } else if (uploadedUrl) {
        // Imagen existente (ya está en S3)
        existingImages.push({
          url: uploadedUrl,
          alt_text: imageControl.get('alt_text')?.value
        });
      }
    }

    // Subir imágenes nuevas si las hay
    let uploadedNewImages: any[] = [];
    if (newImages.length > 0) {
      const archivos = newImages.map(img => img.file);
      const altTexts = newImages.map(img => img.alt_text);

      const uploadedUrls = await this.s3Service.subirVariasImagenes(
        archivos,
        this.currentOrgId,
        (progresoTotal, completadas) => {
          this.uploadProgress.set(progresoTotal);
          this.uploadingCount.set(completadas);
        }
      );

      uploadedNewImages = uploadedUrls.map((resultado, index) => ({
        url: resultado.url,
        alt_text: altTexts[index]
      }));
    }

    // Combinar imágenes existentes + nuevas
    const allImages = [...existingImages, ...uploadedNewImages];

    // 2. Construir payload
    const formValue = this.landingForm.value;
    const payload = {
      org_id: this.currentOrgId,
      title: formValue.title,
      slug: formValue.slug,
      content_json: this.buildContentJson(),
      custom_html: formValue.custom_html || '',
      seo_tags: this.buildSeoTags(allImages),
      images: allImages
    };

    console.log('📤 Enviando payload al backend:', payload);

   const pageId = this.currentPageId; // 👈 Usar pageId guardado
const isEditMode = this.isEditMode();

console.log('🔍 Verificando modo:', { isEditMode, pageId, orgId: this.currentOrgId });

if (isEditMode && pageId) {
  // MODO EDICIÓN - Actualizar
  console.log('🔄 Actualizando landing page...');
  console.log('📍 PageId:', pageId);
  console.log('📍 OrgId:', this.currentOrgId);

  // 👇 USAR pageId y orgId
  this.landingService.updateLandingPage(pageId, this.currentOrgId, payload).subscribe({
    next: (response) => {
      this.submitSuccess.set('¡Página actualizada exitosamente!');

      const slugActualizado = response.page?.slug || payload.slug;
      const baseUrl = window.location.origin;
      const urlCorrecta = `${baseUrl}/sitios/${this.currentOrgId}/${slugActualizado}`;

      this.previewUrl.set(urlCorrecta);

      console.log('✅ Página actualizada:', response);

      this.isSubmitting.set(false);
      this.uploadProgress.set(0);
    },
    error: (error) => {
      console.error('❌ Error actualizando:', error);
      console.error('❌ Detalles completos:', {
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        error: error.error
      });

      this.submitError.set(error.error?.error || 'Error al actualizar la página');
      this.isSubmitting.set(false);
      this.uploadProgress.set(0);
    }
  });

    } else {
      // MODO CREACIÓN - Crear nueva
      console.log('➕ Creando nueva landing page...');

      this.landingService.createLandingPage(payload).subscribe({
        next: (response) => {
          this.submitSuccess.set('¡Página creada exitosamente!');

          const slugCreado = response.page?.slug || payload.slug;
          const baseUrl = window.location.origin;
          const urlCorrecta = `${baseUrl}/sitios/${this.currentOrgId}/${slugCreado}`;

          this.previewUrl.set(urlCorrecta);

          console.log('✅ Página creada:', response);
          console.log('🔗 URL generada:', {
            org_id: this.currentOrgId,
            slug: slugCreado,
            url: urlCorrecta,
            ambiente: baseUrl
          });

          this.isSubmitting.set(false);
          this.uploadProgress.set(0);
        },
        error: (error) => {
          console.error('❌ Error del servidor:', error);
          const errorMsg = error.status === 409
            ? 'Este slug ya existe. Por favor usa uno diferente.'
            : error.error?.error || 'Error al crear la página';
          this.submitError.set(errorMsg);

          this.isSubmitting.set(false);
          this.uploadProgress.set(0);
        }
      });
    }

  } catch (error: any) {
    this.submitError.set(error.message || 'Error al subir las imágenes');
    this.isSubmitting.set(false);
    this.uploadProgress.set(0);
    console.error('❌ Error en onSubmit:', error);
  }
}
}
